/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { useCallback, useState } from "react";

/**
 * useAsyncEvent is a hook that wraps an event handler with loading state. It is
 * mainly useful for showing a loading state on a button click.
 * @param onEvent - The event handler to wrap with loading state.
 */
export function useAsyncEvent(fn?: undefined): [boolean, undefined];
export function useAsyncEvent<T extends (...args: any[]) => any>(
  fn: T,
  invalidators?: unknown[]
): [boolean, T];
export function useAsyncEvent<T extends (...args: any[]) => any>(
  fn?: T,
  invalidators?: unknown[]
): [boolean, T | undefined];
export function useAsyncEvent<T extends (...args: any[]) => any>(
  fn?: T,
  invalidators: unknown[] = []
): [boolean, T | undefined] {
  const [busy, setBusy] = useState(false);

  const handleEvent = useCallback(
    async (...args: any[]) => {
      if (!fn) return;
      try {
        setBusy(true);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        await fn(...args);
        setBusy(false);
      } catch (err) {
        setBusy(false);
        throw err;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fn, ...invalidators] // invalidators are used to re-create the callback when they change
  );

  if (!fn) return [false, undefined] as const;
  return [busy, handleEvent as T] as const;
}
